import { http } from '@/api/client/http';
import { ApiError } from '@/api/client/problem';
import {
  StockBalanceListResponseSchema,
  StockMovementListResponseSchema,
} from '@/api/schemas/inventory';
import type { StockBalance, StockMovement } from '@/api/schemas/inventory';
import { CatalogApi } from '@/modules/catalog';

/**
 * Inventory module service — every API call this module makes passes through here.
 *
 * A STOCK ROW NEEDS A HUMAN LABEL
 * -------------------------------
 * M18 keys a stock balance by the OFFER it stocks (`itemId` is an offer id), so
 * the raw row identifies itself with a uuid. The sku is the human label, and the
 * sku lives on the offer.
 *
 * The two reads have DIFFERENT authorities — `inventory.stock.read` (owner,
 * staff AND viewer) and `catalog.offer.read` (owner and staff only) — so the
 * join is best-effort by design:
 *
 *   - an owner or staff member sees `DEMO-SKU-001`;
 *   - a viewer sees the same row identified by its item id.
 *
 * The second case is NOT a failure and is NOT an empty table: the balance is
 * real, the label the actor may not read is simply absent.
 */
export const InventoryApi = {
  /** GET /inventory/stock-balances — the tenant's balances. */
  listBalances: async (): Promise<StockBalance[]> => {
    const response = await http.get('/inventory/stock-balances', StockBalanceListResponseSchema);
    return response.data;
  },

  /** GET /inventory/stock-movements — the tenant's movements, optionally for one item. */
  listMovements: async (itemId?: string): Promise<StockMovement[]> => {
    const response = await http.get('/inventory/stock-movements', StockMovementListResponseSchema, {
      ...(itemId === undefined ? {} : { query: { itemId } }),
    });
    return response.data;
  },

  /**
   * Resolves offer ids to their skus.
   *
   * Returns an EMPTY map when the caller may not read offers. That is the
   * signal the page uses to fall back to the item id — not an error to report.
   */
  resolveSkus: async (): Promise<Map<string, string>> => {
    try {
      const offers = await CatalogApi.listOffers();
      return new Map(offers.map((offer) => [offer.id, offer.sku]));
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 403) return new Map();
      throw cause;
    }
  },
};
